import React, { useCallback, useContext, useRef } from 'react'
import { connect, ConnectedProps } from 'react-redux'

import {
  TripPlannerStickyHeader,
  TripPlannerHeaderCentreContent,
  TripPlannerStyledLogo,
  FlexBreakpoint,
  LeftContent,
  RightContent,
} from './Shared'
import SaveButtonOnboardingTooltip from '../CuratedTrip/SaveButtonOnboardingTooltip'
import ImmersiveHeaderTripDetails from '../ImmersiveHeader/ImmersiveHeaderTripDetails'
import CopyTemplate from '../TripModal/Templates/CopyTemplate'

import IconButton from 'components/Luxkit/Button/IconButton'
import TextButton from 'components/Luxkit/Button/TextButton'
import ArrowLeftIcon from 'components/Luxkit/Icons/line/LineArrowLeftIcon'
import LineHeartIcon from 'components/Luxkit/Icons/line/LineHeartIcon'
import LineLinkIcon from 'components/Luxkit/Icons/line/LineLinkIcon'
import LineUploadIcon from 'components/Luxkit/Icons/line/LineUploadIcon'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import usePendingLoginHandler from 'hooks/usePendingLoginHandler'
import { copyAvailable, copyValueToClipboard } from 'lib/web/clipboardUtils'
import { useIsTabletAndSmallerScreen } from 'hooks/useScreenSize'
import { HeaderInViewContext } from 'tripPlanner/contexts/HeaderInViewContext'
import useSaveButtonOnboardingTooltipRules from 'tripPlanner/hooks/onboarding/useSaveButtonOnboardingTooltipRules'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { FullTrip } from 'tripPlanner/types/common'

const mapState = (state: App.State) => ({
  headlessMode: state.config.headlessMode,
})

const connector = connect(mapState)

type PropsFromRedux = ConnectedProps<typeof connector>

interface Props extends PropsFromRedux {
  trip: FullTrip
}

function CuratedHeaderFull({ trip, headlessMode }: Props) {
  const saveButtonRef = useRef<HTMLButtonElement | null>(null)
  const rootRef = useRef<HTMLElement>(null)
  const mapHasCollapsed = useIsTabletAndSmallerScreen()

  useHeaderStateObserver('tripPlanner', rootRef)

  const { isTooltipOpen, setTooltipWasDismissed } =
    useSaveButtonOnboardingTooltipRules()

  const openModal = useOpenModal()

  const onSave = useCallback(
    () =>
      openModal(CopyTemplate, {
        template: trip,
      }),
    [openModal, trip],
  )

  const onSaveClick = usePendingLoginHandler(onSave, 'tripPlannerLogin')

  const onShareClick = useCallback(() => {
    copyValueToClipboard(window.location.href)
      .then(() =>
        showSnackbar('Link copied', 'success', { icon: <LineLinkIcon /> }),
      )
      .catch(() => showSnackbar('Unable to copy link', 'warning'))
  }, [])

  const { inView } = useContext(HeaderInViewContext)

  return (
    <TripPlannerStickyHeader ref={rootRef}>
      {inView && !headlessMode && (
        <TextButton
          kind="tertiary"
          variant="dark"
          size="large"
          startIcon={<ArrowLeftIcon />}
          to="/trip-planner/curated"
        >
          Curated trips
        </TextButton>
      )}
      {(!inView || headlessMode) && (
        <LeftContent>
          {!headlessMode && (
            <IconButton
              kind="tertiary"
              variant="dark"
              size="large"
              to="/trip-planner/curated"
              aria-label="Curated trips"
            >
              <ArrowLeftIcon />
            </IconButton>
          )}
          <ImmersiveHeaderTripDetails trip={trip} dateFormat="length" />
        </LeftContent>
      )}

      <TripPlannerHeaderCentreContent to="/">
        {inView && !headlessMode && (
          <FlexBreakpoint max="tablet">
            <TripPlannerStyledLogo />
          </FlexBreakpoint>
        )}
        <FlexBreakpoint min="desktop">
          <TripPlannerStyledLogo />
        </FlexBreakpoint>
      </TripPlannerHeaderCentreContent>
      <RightContent>
        {!inView && !mapHasCollapsed && (
          <CSSBreakpoint min="tablet">
            <TextButton
              onClick={onSaveClick}
              startIcon={<LineHeartIcon />}
              kind="secondary"
              variant="default"
              ref={saveButtonRef}
            >
              Save trip
            </TextButton>
            <SaveButtonOnboardingTooltip
              anchorRef={saveButtonRef}
              isOpen={isTooltipOpen}
              onDismiss={setTooltipWasDismissed}
            />
          </CSSBreakpoint>
        )}
        {copyAvailable() && (
          <TextButton
            kind="tertiary"
            variant="default"
            startIcon={<LineUploadIcon />}
            onClick={onShareClick}
          >
            Share
          </TextButton>
        )}
      </RightContent>
    </TripPlannerStickyHeader>
  )
}

export default connector(CuratedHeaderFull)
